import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, expand, map, reduce, tap } from 'rxjs';
import { UserSummary } from '../users/user-summary';
import {
  ProjectAccessRole,
  ProjectPermissions,
  ProjectResponse,
  ProjectSharingSummary,
  ProjectsApiService,
} from './projects-api.service';
import { JobPollingService } from '../jobs/job-polling.service';
import {
  CancellationResponse,
  JobProgressDetail,
  JobResponse,
  JobsApiService,
  JobStatus,
} from '../jobs/jobs-api.service';
import { AccessSettings } from '../manage-access/manage-access.service';

export type ProjectStatus =
  | 'Queued'
  | 'In Progress'
  | 'Cancelling'
  | 'Complete'
  | 'Failed'
  | 'Cancelled'
  | 'Not Started';

export interface Project {
  id: string;
  name: string;
  fileName: string;
  records: number;
  date: string;
  reviewed: number;
  status: ProjectStatus;
  owner: UserSummary;
  accessRole?: ProjectAccessRole;
  permissions?: ProjectPermissions;
  sharing?: ProjectSharingSummary;
  backendStatus?: string;
  sbmProjectNumber?: string | null;
  jobId?: string;
  jobStatus?: JobStatus;
  jobProgress?: string;
  jobProgressDetail?: JobProgressDetail;
  jobRevision?: number;
}

/**
 * The one project whose Project Details demonstrates the simulated
 * enterprise AG Grid Tool Panel (Columns + Filters sidebar). Every other
 * project renders the plain grid without the panel.
 */
export const TOOL_PANEL_PROJECT_ID = 'seed-6';

/**
 * NE Medical Partners LLC serves the multi-match dataset: the same provided
 * client repeated across rows, each with a different Labcorp account (a 1:1
 * match between provided data and internal accounts is rare in reality).
 * Its grid also sorts ascending by the provided Acct # by default so matches
 * for one client sit together.
 */
export const MULTI_MATCH_PROJECT_ID = 'seed-8';

function mapBackendStatus(backendStatus: string, jobStatus?: JobStatus): ProjectStatus {
  switch (jobStatus) {
    case 'pending':
      return 'Queued';
    case 'running':
      return 'In Progress';
    case 'cancelling':
      return 'Cancelling';
    case 'done':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
  }
  switch (backendStatus) {
    case 'completed':
      return 'Complete';
    case 'processing':
      return 'In Progress';
    case 'ready':
    case 'mapped':
    case 'draft':
    case 'failed':
      return 'Failed';
    default:
      return 'Not Started';
  }
}

@Injectable({ providedIn: 'root' })
export class ProjectsService {
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly jobsApi = inject(JobsApiService);
  private readonly jobPolling = inject(JobPollingService);
  private readonly destroyRef = inject(DestroyRef);
  private hasLoadedOnce = false;

  readonly projects = signal<Project[]>([]);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly loadError = signal<string | null>(null);

  /**
   * Load projects from backend API.
   * Safe to call multiple times - will only load once unless force=true.
   */
  loadProjects(force = false): void {
    // Skip if already loaded and not forcing refresh
    if ((this.hasLoadedOnce || this.loaded()) && !force) {
      return;
    }

    this.loading.set(true);
    this.loadError.set(null);
    this.loadAllProjectPages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (projects) => {
          this.hasLoadedOnce = true;

          this.projects.set(projects.map((project) => this.mapProject(project)));
          this.loaded.set(true);
          this.loading.set(false);

          // Start polling for active jobs
          this.startPollingActiveJobs();
        },
        error: (err: any) => {
          console.error('Failed to load projects:', err);
          this.loadError.set('Your files could not be loaded. Please try again.');
          this.loaded.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadAllProjectPages(): Observable<ProjectResponse[]> {
    const loadPage = (page: number) =>
      this.projectsApi.listProjects({
        page,
        limit: 100,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
    return loadPage(1).pipe(
      expand((response) =>
        response.pagination.page < response.pagination.totalPages
          ? loadPage(response.pagination.page + 1)
          : EMPTY,
      ),
      map((response) => response.projects),
      reduce((projects, page) => [...projects, ...page], [] as ProjectResponse[]),
    );
  }

  private mapProject(project: ProjectResponse): Project {
    return {
      id: project.id,
      name: project.name,
      fileName: project.fileName ?? '',
      records: project.rowCount ?? 0,
      date: project.createdAt.split('T')[0],
      reviewed: 0,
      status: mapBackendStatus(project.status, project.latestJob?.status),
      backendStatus: project.status,
      owner: project.owner,
      accessRole: project.accessRole,
      permissions: project.permissions,
      sharing: project.sharing,
      sbmProjectNumber: project.sbmProjectNumber,
      jobId: project.latestJob?.id,
      jobStatus: project.latestJob?.status,
      jobProgress: project.latestJob?.progress ?? undefined,
      jobProgressDetail: project.latestJob?.progressDetail ?? undefined,
      jobRevision: project.latestJob?.revision ?? 0,
    };
  }

  /**
   * Start polling for active jobs (running or pending status).
   */
  private startPollingActiveJobs(): void {
    const activeProjects = this.projects().filter(
      (p) =>
        p.jobId &&
        (p.jobStatus === 'running' ||
          p.jobStatus === 'pending' ||
          p.jobStatus === 'cancelling'),
    );

    for (const project of activeProjects) {
      if (project.jobId) {
        this.jobPolling.registerJob(project.jobId, project.id, (job: JobResponse) => {
          this.updateProjectJobStatus(project.id, job);
        });
      }
    }
  }

  /**
   * Update a project's job status from polling.
   */
  private updateProjectJobStatus(projectId: string, job: JobResponse): void {
    this.projects.update((projects) =>
      projects.map((p) => {
        if (p.id !== projectId) return p;
        if (
          p.jobStatus === 'cancelling' &&
          (job.status === 'pending' || job.status === 'running')
        ) {
          return p;
        }
        if (job.revision < (p.jobRevision ?? 0)) {
          return p;
        }

        return {
          ...p,
          jobStatus: job.status,
          jobProgress: job.progress ?? p.jobProgress,
          jobProgressDetail: job.progressDetail ?? p.jobProgressDetail,
          jobRevision: job.revision,
          // Update project status based on job status
          backendStatus:
            job.status === 'done'
              ? 'completed'
              : job.status === 'failed'
                ? 'failed'
                : job.status === 'cancelled'
                  ? 'mapped'
                  : p.backendStatus,
          status: mapBackendStatus(p.backendStatus ?? '', job.status),
        };
      }),
    );
  }

  updateSharing(projectId: string, settings: AccessSettings): Project | undefined {
    const memberPreview = (settings.members ?? []).slice(0, 3).map(({ id, displayName }) => ({
      id,
      displayName,
    }));
    let updated: Project | undefined;
    this.projects.update((projects) =>
      projects.map((project) =>
        project.id === projectId
          ? (updated = {
              ...project,
              sharing: {
                level: settings.level,
                memberCount: settings.memberUserIds.length,
                memberPreview: settings.level === 'invite-only' ? memberPreview : [],
              },
            })
          : project,
      ),
    );
    return updated;
  }

  removeProject(id: string): void {
    const removed = this.getById(id);
    this.projects.update((list) => list.filter((p) => p.id !== id));
    this.projectsApi
      .deleteProject(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          console.error('Failed to delete project:', err);
          if (removed) {
            this.projects.update((list) => [removed, ...list]);
          }
        },
      });
  }

  getById(id: string): Project | undefined {
    return this.projects().find((p) => p.id === id);
  }

  /**
   * Start validation job for a project.
   */
  startValidation(projectId: string): Observable<any> {
    return this.projectsApi.startValidation(projectId);
  }

  cancelValidation(projectId: string, jobId: string): Observable<CancellationResponse> {
    const previousProject = this.getById(projectId);
    this.projects.update((projects) =>
      projects.map((project) =>
        project.id === projectId
          ? { ...project, jobStatus: 'cancelling', status: 'Cancelling' }
          : project,
      ),
    );

    return this.jobsApi.requestCancellation(jobId).pipe(
      tap({
        next: (cancellation) => {
          this.projects.update((projects) =>
            projects.map((project) =>
              project.id === projectId
                ? {
                    ...project,
                    jobStatus: cancellation.status === 'cancelled' ? 'cancelled' : 'cancelling',
                    backendStatus:
                      cancellation.status === 'cancelled' ? 'mapped' : project.backendStatus,
                    status:
                      cancellation.status === 'cancelled' ? 'Cancelled' : 'Cancelling',
                  }
                : project,
            ),
          );
        },
        error: () => {
          if (previousProject) {
            this.projects.update((projects) =>
              projects.map((project) =>
                project.id === projectId && project.jobStatus === 'cancelling'
                  ? {
                      ...project,
                      jobStatus: previousProject.jobStatus,
                      backendStatus: previousProject.backendStatus,
                      status: previousProject.status,
                    }
                  : project,
              ),
            );
          }
        },
      }),
    );
  }
}
